<script>
  import { format } from 'svelte-i18n';
  import { Tooltip } from '@sveltestrap/sveltestrap';

  import { Icon } from '@client/components/icon';

  import { formatDate } from '@common/utils/dates';

  /**
   * Callback function to be executed on button click.
   * @type {function}
   * @default () => {}
   */
  export let onClick = () => {};

  /**
   * Information about the currently selected campaign.
   * @type {Object}
   * @default null
   */
  export let selectedCampaign = null;

  /**
   * Information about the currently selected date.
   * @type {Object}
   * @default null
   */
  export let selectedDate = null;

  /**
   * Array containing ordered campaign information for a specific day.
   * @type {Array}
   * @default []
   */
  export let orderedCampaignsForDay = [];

  /**
   * Maximum number of events to allowed to be displayed under ongoing campaigns.
   * @type {number}
   * @default 60
   */
  export let maxEventCount = 60;

  let timeline = {};
  let ongoing = [];

  const selected = new Date(selectedDate.dateLabel);
  const today = new Date();
  const currentHour = today.getHours();
  const isToday = formatDate(today.getTime(), 'MM/dd/yyyy') === formatDate(selected.getTime(), 'MM/dd/yyyy');

  const formatTimelineLabel = (hour) => `${hour % 12 || 12} ${hour < 12 ? 'am' : 'pm'}`;

  const generateTimeline = () => {
    // generate timeline object containing 24 indexes to hold our events
    let series = { ...Array.from({ length: 24 }).map(() => []) };

    selectedDate.events.forEach((event) => {
      if (event.schedule?.startDate) {
        let hour = Number(formatDate(event.schedule.startDate, 'k'));

        if (hour === 24) {
          hour = 0;
        }

        if (series[hour]) {
          // make sure lollipop color is consistent with calendar view campaign dot
          event.color = selectedDate.colors.shift();
          // push our campaign to the correct hourly index of the timeline
          event.ongoing =
            formatDate(event.schedule?.startDate, 'MM/dd/yyyy') !==
            formatDate(new Date(selectedDate.dateLabel), 'MM/dd/yyyy');

          if (event.ongoing) {
            ongoing.push(event);
          } else {
            series[hour].push(event);
          }
          // preload image
          new Image().src = event.previewURL;
        }
      }
    });

    for (let hour in series) {
      if (series[hour].length) {
        series[hour] = series[hour].sort((a, b) => new Date(a.schedule.startDate) - new Date(b.schedule.startDate));
      }
    }

    orderedCampaignsForDay = Object.values(series)
      .filter((values) => !!values)
      .flat(2);

    selectedCampaign = isToday
      ? orderedCampaignsForDay.find(({ schedule }) => {
          return Number(formatDate(schedule.startDate, ' k')) >= currentHour;
        })
      : orderedCampaignsForDay[0];

    return series;
  };

  timeline = generateTimeline();

  $: empty = !Object.keys(timeline)
    .map((item) => timeline[item])
    .flat().length;
</script>

{#if ongoing.length}
  <div class="ongoing-group" data-count={ongoing.length}>
    <div class="ongoing-label">
      {$format('label.ONGOING_CAMPAIGNS')} <Icon name="help-circle" id="ongoing-campaigns-tooltip" />
      <Tooltip target="ongoing-campaigns-tooltip">
        {$format('label.TOOLTIP.ONGOING_CAMPAIGNS')}
      </Tooltip>
    </div>
    <div class="ongoing-items">
      {#if ongoing.length <= maxEventCount}
        <div class="lollipops" style="width: {Math.max(ongoing.length - 1, 0) * 14}px;">
          {#each ongoing as event, j}
            <div
              class="lollipop"
              class:active={selectedCampaign?._id === event?._id}
              style="background-color: #{event.color}; margin-left: {j * 12}px;"
              title={formatDate(event.schedule.startDate)}
              role="button"
              tabindex="0"
              on:click={() => onClick(event)}
            >
              <Icon name="arrow-right" class="text-small" style="color: rgba(0, 0, 0, 0.1)" />
            </div>
          {/each}
        </div>
      {:else}
        <div class="lollipops">
          {$format('label.X_ONGOING_CAMPAIGNS', { values: { count: ongoing.length } })}
        </div>
      {/if}
    </div>
  </div>
{/if}

{#if empty}
  <div class="empty">{$format('label.NO_CAMPAIGNS_SCHEDULED_TODAY')}</div>
{/if}

<div class="timeline">
  {#each Object.entries(timeline) as [hour, events], i}
    <span
      data-hour={hour}
      class:today={isToday}
      class:delivered={(isToday && hour < currentHour) || (!isToday && today > selected)}
      class:last={isToday && Number(hour) === currentHour - 1}
    >
      {#if i !== 0}
        <span class="time">{formatTimelineLabel(hour)}</span>
      {/if}

      {#if events.length}
        <div class="group" data-count={events.length}>
          <div class="lollipops">
            {#each events as event, j}
              <div
                class="lollipop"
                class:active={selectedCampaign?._id === event?._id}
                style="background-color: #{event.color}; margin-left: {j * 3}px;"
                title={formatDate(event.schedule.startDate)}
                on:click={() => onClick(event)}
                role="button"
                tabindex="0"
              >
                {#if (isToday && hour < currentHour) || (!isToday && today > selected)}
                  <Icon name="check" class="text-small" style="color: rgba(0, 0, 0, 0.1)" />
                {/if}
              </div>
            {/each}
          </div>
        </div>
      {/if}
    </span>
  {/each}
</div>

<style lang="scss" src="./timeline.scss"></style>
